import React, { useState, useEffect, useCallback } from 'react'
import { Editor } from '@tiptap/react'
import {
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material'
import {
  Close as CloseIcon,
} from '@mui/icons-material'

interface HeadingItem {
  level: number
  text: string
  pos: number
  id: string
}

interface DocumentOutlineProps {
  editor: Editor | null
  isOpen: boolean
  onClose: () => void
}

const DocumentOutline: React.FC<DocumentOutlineProps> = ({ editor, isOpen, onClose }) => {
  const [headings, setHeadings] = useState<HeadingItem[]>([])
  const [activePos, setActivePos] = useState<number | null>(null)

  const extractHeadings = useCallback(() => {
    if (!editor) return
    const items: HeadingItem[] = []
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'heading' && node.textContent.trim()) {
        items.push({
          level: node.attrs.level,
          text: node.textContent.trim(),
          pos,
          id: `heading-${pos}`,
        })
      }
    })
    setHeadings(items)
  }, [editor])

  useEffect(() => {
    if (!editor || !isOpen) return
    extractHeadings()

    const handler = () => extractHeadings()
    editor.on('update', handler)
    return () => { editor.off('update', handler) }
  }, [editor, isOpen, extractHeadings])

  useEffect(() => {
    if (!editor || !isOpen) return
    const handler = () => {
      const { from } = editor.state.selection
      let closest: HeadingItem | null = null
      for (const h of headings) {
        if (h.pos <= from) closest = h
        else break
      }
      setActivePos(closest?.pos ?? null)
    }
    editor.on('selectionUpdate', handler)
    return () => { editor.off('selectionUpdate', handler) }
  }, [editor, isOpen, headings])

  const scrollToHeading = (pos: number) => {
    if (!editor) return
    editor.chain().focus().setTextSelection(pos + 1).run()
    const domAtPos = editor.view.domAtPos(pos + 1)
    if (domAtPos?.node) {
      const el = domAtPos.node instanceof HTMLElement ? domAtPos.node : domAtPos.node.parentElement
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  if (!isOpen) return null

  return (
    <Box sx={{
      width: 260,
      flexShrink: 0,
      borderLeft: '1px solid',
      borderColor: 'divider',
      backgroundColor: '#fafafa',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 600, color: '#1a365d' }}>
          Navegação
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {headings.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Adicione títulos (H1, H2, H3) ao documento para ver a estrutura aqui.
            </Typography>
          </Box>
        ) : (
          headings.map((h) => (
            <Box
              key={h.id}
              onClick={() => scrollToHeading(h.pos)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                px: 1,
                py: 0.5,
                pl: 1 + (h.level - 1) * 1.5,
                cursor: 'pointer',
                borderLeft: activePos === h.pos ? '3px solid #1976d2' : '3px solid transparent',
                backgroundColor: activePos === h.pos ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                transition: 'all 0.15s',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: h.level === 1 ? 12 : h.level === 2 ? 11.5 : 11,
                  fontWeight: h.level <= 2 ? 600 : 400,
                  color: activePos === h.pos ? '#1976d2' : 'text.primary',
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {h.text}
              </Typography>
            </Box>
          ))
        )}
      </Box>

      {headings.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 1.5, py: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {headings.length} {headings.length === 1 ? 'título' : 'títulos'}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  )
}

export default DocumentOutline
